import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import Tag from "@/components/base/Tag";
import SectionHeading from "@/components/base/SectionHeading";
import ShareButton from "@/components/feature/ShareButton";
import { getMediaById, getRelatedMedia } from "@/lib/catalog";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getMediaById(id) : undefined;

  useEffect(() => {
    if (item) {
      document.title = `${item.title}（${item.year}） - 麻豆网站`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", item.summary);
    }
  }, [item]);

  if (!item) {
    return (
      <div className="w-full px-4 md:px-6 py-24 flex flex-col items-center justify-center text-center">
        <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-5" />
        <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2">影视资源不存在或已下架</h1>
        <p className="text-sm text-foreground-500 mb-6">你访问的影视内容可能已被移除，去首页看看其他精彩内容吧。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedMedia(item, 4);
  const typeLabel = item.type === "movie" ? "电影" : "电视剧";

  const metaRows = [
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: "年份", value: String(item.year) },
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join("、") },
    { label: item.type === "movie" ? "片长" : "集数", value: item.duration },
  ];

  return (
    <div className="w-full">
      {/* 顶部背景 */}
      <div className="relative w-full">
        <div className="absolute inset-0 h-full overflow-hidden">
          <img src={item.backdrop} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-primary-950/85" />
        </div>
        <div className="relative z-10 px-4 md:px-6 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-primary-200 mb-6 flex-wrap">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${item.type}`} className="hover:text-accent-400 transition-colors">{typeLabel}</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{item.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-56 flex-shrink-0 self-center md:self-start">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-primary-800 border border-primary-700">
                <img
                  src={item.cover}
                  alt={`${item.title}海报`}
                  title={`${item.title} - ${item.genres.join(" ")}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-accent-500 text-foreground-950">{typeLabel}</span>
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-700 text-background-50">{item.region}</span>
                <span className="text-sm font-bold text-accent-400">
                  <i className="ri-star-fill mr-1" />
                  {item.rating.toFixed(1)} 分
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-black text-background-50 mb-3">{item.title}</h1>
              <p className="text-sm md:text-base text-primary-100/90 leading-relaxed mb-5 max-w-3xl">{item.summary}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-6 max-w-2xl">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <span className="text-primary-300 flex-shrink-0 w-12">{row.label}</span>
                    <span className="text-background-50/95 min-w-0">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="w-full flex flex-wrap items-center gap-3">
                <Link
                  to={`/category/${item.type}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg" />
                  浏览同类{typeLabel}
                </Link>
                <ShareButton title={item.title} text={item.summary} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-10 md:py-14">
        {/* 简介 */}
        <section className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">剧情概要</h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-4xl">{item.synopsis}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            {item.tags.map((tag) => (
              <Tag key={tag} label={tag} tone="accent" href={`/category/${item.type}`} />
            ))}
            {item.genres.map((g) => (
              <Tag key={g} label={g} tone="muted" href={`/category/${item.type}`} />
            ))}
          </div>
        </section>

        {/* 剧照 */}
        <section className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">精彩剧照</h2>
          <div className="flex gap-4 overflow-x-auto scroll-hide pb-2 -mx-4 px-4 md:mx-0 md:px-0">
            {item.photos.map((photo, i) => (
              <div
                key={i}
                className="relative w-72 md:w-80 flex-shrink-0 aspect-video rounded-lg overflow-hidden bg-background-200 border border-background-200"
              >
                <img
                  src={photo}
                  alt={`${item.title}剧照 ${i + 1}`}
                  title={`${item.title} 剧照`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            ))}
          </div>
        </section>

        {/* 相关推荐 */}
        <section>
          <SectionHeading
            eyebrow="You May Also Like"
            title="相关推荐"
            description="基于类型与地区为你匹配的影视作品"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {related.map((rel) => (
              <MediaCard key={rel.id} item={rel} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}